import React, { useMemo, useEffect, useRef } from 'react';
import posthog from 'posthog-js';
import dayjs from 'dayjs';
import { Link } from 'react-router';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import LanguageIcon from '@mui/icons-material/Language';
import { useAppSelector } from '../../../state/store';
import { getActiveMarker, getLocationsById, getSensorsById } from '../../../state/selectors';
import {
  ContactMethod,
  ContactType,
  createEmailTemplate,
  GenericContactDetails,
  TFLContactDetails,
} from './ContactDetails';
import { varNameDetails } from '../../../utils/varNames';
import { themeProps } from '../../../styles/theme';
import { getDataValueString } from '../../../Widgets/HelperComponents/DataValueString';
import { getDataBandParams } from '../../../utils/dataBandParams';

interface ReportIssueDialogProps {
  sensorId: string;
  showDialog: boolean;
  closeDialog: () => void;
}

function ReportIssueDialog({ sensorId, showDialog, closeDialog }: ReportIssueDialogProps) {
  const theme = useTheme();
  const locationsById = useAppSelector(getLocationsById);
  const sensorsById = useAppSelector(getSensorsById);
  const activeMarker = useAppSelector(getActiveMarker);
  const hasCaptured = useRef(false);

  const sensorDetails = sensorsById.get(sensorId) ?? { id: sensorId };

  const { location, name } = sensorDetails;

  const sensorValue = useMemo(() => {
    let value;
    const dataItemValue = sensorDetails?.data?.find((item) => item.varName === activeMarker)?.value;
    if (dataItemValue !== undefined) value = dataItemValue;
    return value;
  }, [activeMarker, sensorDetails?.data]);

  const locFullName = useMemo(() => {
    let loc = location;
    const propLocItem = locationsById.get(location ?? '');
    const locName = propLocItem?.name;
    if (location && location === '#') loc = locName;
    else if (location && location.startsWith('#')) {
      const parentLocName = locationsById.get(propLocItem?.location ?? '')?.name;
      loc = `${parentLocName}, ${locName}`;
    }

    return loc ?? '';
  }, [location, locationsById]);

  useEffect(() => {
    if (hasCaptured.current) return;
    if (!sensorDetails || sensorValue === undefined || !locFullName || !activeMarker) return; // guard: wait

    posthog.capture('Report Issue Button Clicked', {
      sensorId,
      sensorName: sensorDetails?.name,
      sensorLocation: locFullName, // set full location e.g. 5 Endeavour Square, 10th Floor
      locationId: sensorDetails?.location,
      varName: activeMarker,
      value: getDataValueString(sensorValue, activeMarker),
    });
    hasCaptured.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMarker, locFullName, sensorId, sensorValue]);

  const getContactDetails = () => {
    let contactDetails = GenericContactDetails;
    const isTFLLoc = locationsById.get('#')?.name === '14 Pier Walk'; // Todo: make it dynamic through API where # to be replaced with locationId
    if (isTFLLoc) contactDetails = TFLContactDetails;
    return contactDetails;
  };

  const contactDetails = getContactDetails();
  const { preferredText, contactMethods } = contactDetails;
  // const emailSubject = `${varNameDetails[activeMarker].label} ${name} ${selectedBand?.label}`;
  const emailSubject = `[template] ${varNameDetails[activeMarker].label} ${locFullName}`;

  // move preferred method to beginning
  if (contactMethods) {
    const preferredMethodIndex = contactMethods.findIndex((item) => item.isPreferred === true);
    contactMethods.unshift(contactMethods.splice(preferredMethodIndex, 1)[0]);
  }

  const showContactInfo = (contactMethod: ContactMethod) => {
    let Icon = EmailIcon;
    let url = '';

    switch (contactMethod.type) {
      case ContactType.email:
        url = createEmailTemplate(
          contactMethod.value,
          emailSubject,
          locFullName,
          name ?? '', // using sensor name as category
          sensorValue,
          activeMarker
        );
        break;
      case ContactType.phone:
        Icon = PhoneIphoneIcon;
        url = `tel:${contactMethod.value}`;
        break;
      case ContactType.web:
        Icon = LanguageIcon;
        url = contactMethod.value;
        break;
      default:
        break;
    }

    return (
      <Box
        key={contactMethod.type}
        sx={{ display: 'flex', justifyContent: 'center', marginTop: '5px' }}
      >
        <br />
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <Link
          to="#"
          onClick={(e) => {
            window.location.href = url;
            e.preventDefault();
          }}
          style={{ color: theme.palette.text.primary, display: 'flex' }}
        >
          <Icon /> {contactMethod.displayValue}{' '}
        </Link>
        {contactMethod.additionalInfo && <span>&nbsp;({contactMethod.additionalInfo})</span>}
      </Box>
    );
  };

  const dataBand = getDataBandParams(activeMarker, sensorValue ?? 0);

  return (
    <Dialog open={showDialog} style={{ textAlign: 'center' }} onClose={() => closeDialog}>
      <DialogContent>
        {preferredText}
        <br />
        {contactMethods && contactMethods?.map((method) => showContactInfo(method))}
        <br />
        <b>Detail:</b>
        <br /> Near <b>{name}</b> at <b>{dayjs().format('MMM DD, YYYY HH:mm')}</b> and the{' '}
        {varNameDetails[activeMarker].label} is{' '}
        <b>
          {dataBand.label} {`(${sensorValue} ${varNameDetails[activeMarker].metric})`}
        </b>
        <br /> <br />
        <Typography variant="body2" sx={{ fontSize: '10px' }}>
          If you would like to share feedback about the app itself please contact the LightFi team
          via email {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <Link
            to="#"
            onClick={(e) => {
              window.location.href = 'mailto:support@lightfi.io';
              e.preventDefault();
            }}
            style={{ color: theme.palette.text.primary }}
          >
            support@lightfi.io
          </Link>
        </Typography>
        <br />
        <Button
          variant={themeProps.btnVariant.default}
          sx={{
            margin: '10px',
            width: '50%',
            background: themeProps.colors.warningRed,
            border: 'none',
          }}
          onClick={() => closeDialog()}
        >
          Close
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export default ReportIssueDialog;
